import React, { useState, useEffect, useCallback } from 'react';
import '../../pages/SurveyStart.scss';
import { useDispatch, useSelector } from 'react-redux';
import { submitSurveyAnswerAction, isLastQuestionAction } from '../../redux/attemptSurvey/attemptSurvey.actions';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import sendToken from '../../helper/sendToken';
export default function SurveyQuestion({ question, options, webUrls, urls, onNext, onPrevious, questionIndex, setSelectedOption, setSelectedOptions, selectedOption, selectedOptions, questionList, mandatory, survey, comment, questionType, isAttempted }) {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state?.attemptSurveyInfo);
  const [multipleChoices, setMultipleChoices] = useState([]);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [questionComment, setQuestionComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);

  const language = localStorage.getItem('language') || 'Italian';


  const texts = {
    Italian: {
      selectOptionError: "Seleziona un'opzione",
      selectAtLeastOneOptionError: "Seleziona almeno un'opzione",
      selectRatingError: "Seleziona una valutazione",
      question: "Domanda",
      skip: "Salta",
      rateThisQuestion: "Valuta questa domanda",
      poor: "Scarso",
      sufficient: "Sufficiente",
      good: "Buono",
      veryGood: "Molto Buono",
      excellent: "Eccellente",
      addComment: "Aggiungi il tuo commento",
      loading: "Caricamento...",
      saveAndNext: "Salva e Avanti",
      mandatoryQuestionError: "Questa domanda è obbligatoria",
      submissionFailed: "Invio della risposta fallito",
      previous: "Indietro",
      next: "Avanti"
    },
    English: {
      selectOptionError: "Please select an option",
      selectAtLeastOneOptionError: "Please select at least one option",
      selectRatingError: "Please select a rating",
      question: "Question",
      skip: "Skip",
      rateThisQuestion: "Rate this question",
      poor: "Poor",
      sufficient: "Sufficient",
      good: "Good",
      veryGood: "Very Good",
      excellent: "Excellent",
      addComment: "Add your comment",
      loading: "Loading...",
      saveAndNext: "Save and Next",
      mandatoryQuestionError: "This question is mandatory",
      submissionFailed: "Submission failed",
      previous: "Previous",
      next: "Next"
    }
  };

  // Null-safe option equality check used across UI and handlers
  const optionsEqual = (a, b) => {
    if (a == null || b == null) return false;
    const aIsObj = typeof a === 'object';
    const bIsObj = typeof b === 'object';

    if (aIsObj && bIsObj) {
      if (a._id != null && b._id != null) return a._id === b._id;
      if (a.option != null && b.option != null) return a.option === b.option;
      if (a.option != null && b._id != null) return a.option === b._id;
      if (a._id != null && b.option != null) return a._id === b.option;
    }

    if (aIsObj && !bIsObj && a.option != null) return a.option === b;
    if (!aIsObj && bIsObj && b.option != null) return a === b.option;
    // Handle when a is an ID string/number and b is option object with _id
    if (!aIsObj && bIsObj && b._id != null) return String(a) === String(b._id);

    return a === b;
  };

  const currentQuestionType = questionList[questionIndex]?.questionType?.type || "Single Choice";
  const showCommentSection = questionList[questionIndex]?.comment || false;
  const isLastQuestion = questionIndex === questionList.length - 1;
  const isFirstQuestion = questionIndex === 0;

  // Function to save data to localStorage
  const saveToLocalStorage = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  // Function to retrieve data from localStorage
  const getFromLocalStorage = (key) => {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  };

  // Function to clear all survey data from localStorage
  const clearSurveyDataFromLocalStorage = () => {
    for (let i = 0; i < questionList.length; i++) {
      localStorage.removeItem(`surveyQuestion_${i}`);
    }
  };

  // Load previous data when component mounts or question index changes
  useEffect(() => {
    const savedData = getFromLocalStorage(`surveyQuestion_${questionIndex}`);
    if (savedData) {
      if (currentQuestionType === "Multiple Choice") {
        setMultipleChoices(savedData.answer);
        if (typeof setSelectedOptions === 'function') {
          setSelectedOptions(savedData.answer);
        }
      } else if (currentQuestionType === "Rating") {
        // savedData.answer contains option id(s). Map the first id back to its star index
        const firstId = Array.isArray(savedData.answer) ? savedData.answer[0] : savedData.answer;
        const idx = Array.isArray(options)
          ? options.findIndex(opt => String(opt?._id) === String(firstId))
          : -1;
        const restoredRating = idx >= 0 ? idx + 1 : 0;
        setRating(restoredRating);
        if (restoredRating > 0 && Array.isArray(options)) {
          const optionForRating = options[restoredRating - 1] || null;
          setSelectedOption(optionForRating || null);
        } else {
          setSelectedOption(null);
        }
      } else {
        setSelectedOption(savedData.answer[0]);
      }
      if (showCommentSection) {
        setQuestionComment(savedData.comment || '');
      }
      // Check if survey is completed
      const surveyCompleted = getFromLocalStorage('surveyCompleted');
      if (surveyCompleted) {
        setIsSurveyCompleted(true);
      }
    } else {
      setSelectedOption(null);
      setMultipleChoices([]);
      setRating(0);
      setQuestionComment('');
    }
  }, [questionIndex, currentQuestionType, showCommentSection, options]);

  // Keep local multipleChoices in sync with parent-provided selectedOptions when navigating back/forward
  useEffect(() => {
    if (currentQuestionType === "Multiple Choice" && Array.isArray(selectedOptions)) {
      setMultipleChoices(selectedOptions);
    }
  }, [selectedOptions, currentQuestionType]);

  // Synchronized state update for rating
  useEffect(() => {
    if (currentQuestionType !== "Rating") {
      return;
    }

    if (rating > 0 && Array.isArray(options)) {
      const optionForRating = options[rating - 1] || null;
      setSelectedOption(optionForRating || null);
    } else {
      setSelectedOption(null);
    }
  }, [rating, currentQuestionType, options, setSelectedOption]);

  // Memoize option change handler to prevent unnecessary re-renders
  const handleOptionChange = useCallback((opt, index, event) => {
    if (event) {
      // Prevent default only for links and form submissions
      if (event.target.tagName === 'A' || event.target.tagName === 'FORM') {
        event.preventDefault();
      }
      // Stop propagation to prevent event bubbling
      event.stopPropagation();
    }

    if (isSurveyCompleted) return; // Disable changes if survey is completed or question is attempted

    if (currentQuestionType === "Multiple Choice") {
      setMultipleChoices(prevChoices => {
        const optionIndex = prevChoices.findIndex(choice => optionsEqual(choice, opt));
        if (optionIndex === -1) {
          const newChoices = [...prevChoices, opt];
          if (typeof setSelectedOptions === 'function') {
            setSelectedOptions(newChoices);
          }
          return newChoices;
        } else {
          const newChoices = [...prevChoices];
          newChoices.splice(optionIndex, 1);
          if (typeof setSelectedOptions === 'function') {
            setSelectedOptions(newChoices);
          }
          return newChoices;
        }
      });
    } else {
      setSelectedOption(opt);
    }
  }, [currentQuestionType, setSelectedOption, isSurveyCompleted]);

  // Memoize rating change handler
  const handleRatingChange = useCallback((value) => {
    if (isSurveyCompleted) return; // Disable changes if survey is completed or question is attempted

    setRating(value);
  }, [isSurveyCompleted]);

  const handleCommentChange = useCallback((e) => {
    if (isSurveyCompleted) return; // Disable changes if survey is completed or question is attempted

    setQuestionComment(e.target.value);
  }, [isSurveyCompleted]);

  const validateAnswer = () => {
    if (mandatory) {
      if (currentQuestionType === "Multiple Choice" && !multipleChoices.length) {
        toast.error(texts[language].selectAtLeastOneOptionError);
        return false;
      } else if (currentQuestionType === "Rating" && rating === 0) {
        toast.error(texts[language].selectRatingError);
        return false;
      } else if ((currentQuestionType === "Single Choice" ||
        questionType === "Preliminary" ||
        questionType === "Characteristics") &&
        !selectedOption) {
        toast.error(texts[language].selectOptionError);
        return false;
      }
    } else if (questionType === "Preliminary" && !selectedOption) {
      toast.error(texts[language].selectOptionError);
      return false;
    } else if (questionType === "Characteristics" && !selectedOption) {
      toast.error(texts[language].selectOptionError);
      return false;
    }
    return true;
  };

  const handleSubmitSurveyAnswer = async () => {
    // Prevent double submission
    if (isSubmitting || isLoading) {
      return;
    }

    // Validate answer before submission
    if (!validateAnswer()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const answerToSubmit = buildAnswerIdsForSubmission();

      // Save current answer to localStorage
      saveToLocalStorage(`surveyQuestion_${questionIndex}`, {
        answer: answerToSubmit,
        comment: showCommentSection ? questionComment : ''
      });

      // Create a promise for the answer submission
      await dispatch(submitSurveyAnswerAction(
        survey,
        questionList[questionIndex]?._id,
        answerToSubmit,
        showCommentSection ? questionComment : comment
      ));

      if (isLastQuestion) {
        await dispatch(isLastQuestionAction(survey));
        clearSurveyDataFromLocalStorage();
        setIsSurveyCompleted(true);
      }

      // Only proceed to next question after state updates are complete
      onNext();
    } catch (err) {
      toast.error(texts[language].submissionFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (mandatory) {
      toast.error(texts[language].mandatoryQuestionError);
      return;
    }

    setIsSubmitting(true);

    dispatch(submitSurveyAnswerAction(
      survey,
      questionList[questionIndex]?._id,
      [],
      '',
      true
    ))
      .then(() => {
        if (isLastQuestion) {
          return dispatch(isLastQuestionAction(survey));
        }
      })
      .then(() => {
        onNext();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  const handleNext = () => {
    if (isSurveyCompleted) {
      onNext();
    } else if (!validateAnswer() && !mandatory) {
      handleSkip();
    } else if (validateAnswer()) {
      handleSubmitSurveyAnswer();
    }
  };

  const handleWebUrlClick = (url, data) => {
    // Append data as query parameters
    window.open(url, '_blank');
  };

  const currentToken = localStorage.getItem('Authorization');

  const ratingLabels = {
    1: texts[language].poor,
    2: texts[language].sufficient,
    3: texts[language].good,
    4: texts[language].veryGood,
    5: texts[language].excellent
  };

  const getRatingHelperText = () => {
    if (rating <= 0) {
      return texts[language].rateThisQuestion;
    }
    return ratingLabels[rating] || texts[language].rateThisQuestion;
  };

  const renderQuestionByType = () => {
    switch (currentQuestionType) {
      case "Rating":
        return (
          <div className="star_wrapper">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                className={`star ${rating >= i ? 'active' : ''} ${hoveredRating >= i ? 'hovered' : ''}`}
                key={`rating-${i}-${questionIndex}`}
                onClick={() => handleRatingChange(i)}
                onMouseEnter={() => setHoveredRating(i)}
                onMouseLeave={() => setHoveredRating(0)}
              >
                <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.2699 2.96333L25.5943 9.61222C26.0477 10.5378 27.2566 11.4255 28.2766 11.5955L34.3021 12.5967C38.1554 13.2389 39.0621 16.0344 36.2854 18.7922L31.601 23.4767C30.8077 24.27 30.3732 25.8 30.6188 26.8955L31.9599 32.6944C33.0177 37.2844 30.581 39.06 26.5199 36.6611L20.8721 33.3178C19.8521 32.7133 18.171 32.7133 17.1321 33.3178L11.4843 36.6611C7.44211 39.06 4.98655 37.2656 6.04433 32.6944L7.38544 26.8955C7.631 25.8 7.19655 24.27 6.40322 23.4767L1.71877 18.7922C-1.039 16.0344 -0.151226 13.2389 3.70211 12.5967L9.72766 11.5955C10.7288 11.4255 11.9377 10.5378 12.391 9.61222L15.7154 2.96333C17.5288 -0.644452 20.4754 -0.644452 22.2699 2.96333Z" />
                </svg>
              </div>
            ))}
          </div>
        );

      case "Multiple Choice":
        return (
          <div className="option_wrapper">
            {options?.map((opt, index) => (
              <div
                key={`multiple-${index}-${questionIndex}`}
                style={{
                  width: '100%',
                  margin: '0 0',
                  padding: '12px 0',
                  borderRadius: '8px',
                }}
              >
                <label
                  htmlFor={`option-${questionIndex}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    flex: '1',
                    fontSize: '16px',
                    fontWeight: multipleChoices.some(choice => optionsEqual(choice, opt)) ? '500' : 'normal'
                  }}>
                    {`${index + 1}. ${opt.option || opt}`}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    borderRadius: "50%",
                    color: "white"
                  }} >
                    <div className="right_for_checkbox">
                      {webUrls && webUrls[index] && (
                        <a
                          href="#"
                          className="web_url_button_for_checkbox"
                          onClick={(e) => {
                            e.preventDefault();
                            // handleWebUrlClick(webUrls[index]);
                            sendToken(currentToken, urls[index]);
                          }}
                        >
                          <img className="web_url_icon_for_checkbox" src="../images/ic_more_info.png" alt="Web URL" />
                        </a>
                      )}
                    </div>
                    <div style={{ marginLeft: '10px' }} >
                      <input
                        type="checkbox"
                        id={`option-${questionIndex}-${index}`}
                        name={`question-${questionIndex}`}
                        value={opt}
                        checked={multipleChoices.some(choice => optionsEqual(choice, opt))}
                        onChange={() => handleOptionChange(opt, index)}
                        disabled={isSurveyCompleted}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        );

      default: // Single Choice
        return (
          <div className="option_wrapper">
            {options?.map((opt, index) => (
              <div key={`single-${index}-${questionIndex}`} className="option_container">
                <label className="option" htmlFor={`option-${questionIndex}-${index}`}>
                  <input
                    type="radio"
                    id={`option-${questionIndex}-${index}`}
                    name={`question-${questionIndex}`}
                    value={opt}
                    checked={optionsEqual(selectedOption, opt)}
                    onChange={() => handleOptionChange(opt, index)}
                    disabled={isSurveyCompleted}
                  />
                  <div className="text">{`${index + 1}. ${opt.option || opt}`}</div>

                  <div className="right">
                    {webUrls && webUrls[index] && (
                      <a
                        href="#"
                        className="web_url_button"
                        onClick={(e) => {
                          e.preventDefault();
                          // handleWebUrlClick(webUrls[index]);
                          sendToken(currentToken, urls[index]);
                        }}
                      >
                        <img className="web_url_icon" src="../images/ic_more_info.png" alt="Web URL" />
                      </a>
                    )}
                    <div className="tick"></div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        );
    }
  };

  // Map current UI selection to backend-required answer (array of option _id strings)
  const buildAnswerIdsForSubmission = () => {
    if (currentQuestionType === "Multiple Choice") {
      const ids = multipleChoices
        .map(sel => {
          const match = options?.find(opt => optionsEqual(opt, sel));
          return match?._id || sel?._id || null;
        })
        .filter(Boolean)
        .map(id => id.toString());
      return ids;
    }

    if (currentQuestionType === "Rating") {
      // Map 1..5 to options[0..4]
      const opt = Array.isArray(options) ? options[rating - 1] : null;
      const id = opt?._id || null;
      return id ? [id.toString()] : [];
    }

    // Single Choice and other types default to first selected
    const match = options?.find(opt => optionsEqual(opt, selectedOption));
    const id = match?._id || (selectedOption && selectedOption._id) || null;
    return id ? [id.toString()] : [];
  };

  return (
    <div className="survey-question-container">
      <div className="title">
        <button>
          {language === 'Italian' ? texts[language].question : texts[language].question}
        </button>
      </div>

      <div className="question_card">
        <div className="question-type" style={{ marginBottom: '10px' }}>
          {language === 'Italian' ? 'Tipo di Domanda: ' : 'Question Type: '} {
            language === 'Italian' ? (
              currentQuestionType === 'Multiple Choice' ? 'Scelta multipla' :
                currentQuestionType === 'Single Choice' ? 'Scelta singola' :
                  currentQuestionType === 'Rating' ? 'Valutazione' :
                    currentQuestionType
            ) : currentQuestionType
          }
        </div>
        <div className="question_count">
          <div className="counts">
            <span>{questionIndex + 1}</span>/{questionList?.length}
          </div>
          {!mandatory && (
            <div
              className="skip"
              style={{ cursor: 'pointer' }}
              onClick={handleSkip}
            >
              {texts[language].skip}
            </div>
          )}
        </div>
        <div className="question">{`${questionIndex + 1}. ${question}`}</div>
        {renderQuestionByType()}
        {currentQuestionType === "Rating" && (
          <div className="rating_text">
            {getRatingHelperText()}
          </div>
        )}
        {showCommentSection && (
          <div className="comment_wrapper">
            <textarea
              name="comment"
              id={`comment-${questionIndex}`}
              placeholder={texts[language].addComment}
              value={questionComment}
              onChange={handleCommentChange}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '15px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontFamily: 'inherit',
                resize: 'vertical',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                background: '#fafafa',
                marginTop: '15px',
                outline: 'none'
              }}
              disabled={isSurveyCompleted}
            />
          </div>
        )}
      </div>
      <div className="action center">
        <button className='left' onClick={handlePrevious} >
          <img src={`${process.env.PUBLIC_URL}/images/survey/back.png`} alt="" />
        </button>
        <button
          type="button"
          disabled={isSubmitting || isLoading}
          onClick={handleNext}
        >
          {(isLoading || isSubmitting)
            ? texts[language].loading
            : isSurveyCompleted ? texts[language].next : texts[language].saveAndNext}
        </button>
      </div>
    </div>
  );
}
